import WebViewerClass, { Core, WebViewerInstance } from '@pdftron/webviewer';
import {
  MutableRefObject,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useTimeoutFn } from 'react-use';
import ProjectContext from '../contexts/ProjectContext';
import { getDownloadUrl } from '../utils/api/upload';
import xfdfApi from '../utils/api/xfdf';
import { AnnotationApi, useAnnotationApi } from './annotationApi';
import { useEnvConfig } from './envConfig';
import { v4 as uuidv4 } from 'uuid';
import { toast } from 'react-toastify';

type PdfEditorProps = {
  upload: {
    id: number;
    guid: string;
    canEdit: boolean;
    canDownload?: boolean;
    disableDownload?: boolean;
    tenantId?: number;
  };
  ref: MutableRefObject<HTMLDivElement | null>;
  onClose: Function;
};

export type EditorStatus = 'loading' | 'saving' | 'loaded' | 'saved';
const priviledgedUserRoles = ['Admin', 'Super Admin', 'Divisions Admin', 'Client Manager'];
/**
 * @param param0
 * @returns
 */

export function usePdfEditor({ upload, ref, onClose }: PdfEditorProps): {
  status: EditorStatus;
} {
  const projectId = useProjectId();
  const annotationApi = useAnnotationApi(upload.id, upload.tenantId, projectId);
  const licenseKey = useEnvConfig('WEBVIEWER_KEY');
  const publicUrl = useEnvConfig('PUBLIC_URL');
  const [viewer, setViewer] = useState<null | WebViewerInstance>(null);
  const [annotationsLoaded, setAnnotationsLoaded] = useState(false);
  const [editorStatus, setEditorStatus] = useState<EditorStatus>('loading');
  const settleEditorStatus = useTimeoutFn(() => {
    if (viewer) {
      setEditorStatus('loading');
    }
  }, 1000);

  // Track annotations that existed from previous sessions
  const previousSessionAnnotationIds = useRef<Set<string>>(new Set());

  const handleDownload = async (viewerInstance: WebViewerInstance | null) => {
    await annotationApi.index();
    const { annotationManager } = viewerInstance!.Core;
    annotationManager.getAnnotationsList().forEach((annot) => {
      annot.Locked = true;
      annot.ReadOnly = true;
      annot.LockedContents = true;
      annotationManager.redrawAnnotation(annot);
    });
    viewerInstance!.UI.downloadPdf({
      filename: uuidv4(),
      flags: viewerInstance!.Core.SaveOptions.LINEARIZED,
      downloadType: 'pdf',
      useDisplayAuthor: true,
    });
    setEditorStatus('saved');
    settleEditorStatus[2]();

    return loadRemoteAnnotations(
      annotationApi,
      viewerInstance!,
      previousSessionAnnotationIds,
      false,
    );
  };

  useEffect(() => {
    if (ref.current && !viewer) {
      const element = ref.current;

      WebViewerClass.Iframe(
        {
          path: `${publicUrl}/libs/Webviewer`,
          licenseKey: licenseKey,
          extension: 'pdf',
          isAdminUser: true,
          annotationUser: annotationApi.authorName,
          isReadOnly: !upload.canEdit,
        },
        element,
      ).then((viewerInstance) => {
        setViewer(viewerInstance);
        const { annotationManager, documentViewer } = viewerInstance.Core;

        viewerInstance.UI.disableElements([
          //as per current requirement, removing Forms tab from webviewer temporarily.
          'formFieldEditButton',
          'formFieldEditPanel',
          'toolbarGroup-Forms',
          'printButton', 
          'saveAsButton',
        ]);

        documentViewer.loadDocument(getDownloadUrl(upload.guid, null, true));

        documentViewer.addEventListener('documentLoaded', () => {
          setEditorStatus('loaded');
        });

        if (upload.disableDownload) {
          const { disableElements } = viewerInstance.UI;
          disableElements(['downloadButton']);
        } else if (upload.canDownload) {
          documentViewer.addEventListener('annotationsLoaded', async () => {
            setAnnotationsLoaded(true);
            const annotatations = await annotationApi.index();
            const data = annotatations;
            const annotationCommands = data.filter((d) => d.isCommand);
            annotationCommands.map(async (row) => {
              const annotations =
                await annotationManager.importAnnotationCommand(
                  row.xfdfValue!,
                );
              await annotationManager.drawAnnotationsFromList(
                annotations,
              );
            });

            const options = {
              filename: uuidv4(),
              flags: viewerInstance.Core.SaveOptions.LINEARIZED,
              downloadType: 'pdf',
              useDisplayAuthor: true,
            };

            viewerInstance.UI.downloadPdf(options).then(() => {
              toast('Download Completed');
              setTimeout(() => {
                onClose();
              }, 100);
            });
          });
        } else {
          annotationManager.addEventListener('fieldChanged', async () => {
            const xfdf = await annotationManager.exportAnnotationCommand();
            const mainStr = xfdf.replace(/\n/g, '');
            setEditorStatus('saving');

            await annotationApi.save({
              isCommand: true,
              key: uuidv4(),
              xfdfValue: mainStr,
              projectId,
            });

            setEditorStatus('saved');
            settleEditorStatus[2]();
          });

          annotationManager.addEventListener(
            'annotationChanged',
            (annotations, action, info) => {
              onAnnotationChange(
                viewerInstance,
                async (model) => {
                  setEditorStatus('saving');

                  await annotationApi.save(model);

                  setEditorStatus('saved');
                  settleEditorStatus[2]();
                },
                annotations,
                action,
                info,
                projectId,
                annotationApi,
                previousSessionAnnotationIds,
              );
            },
          );

          documentViewer.addEventListener('annotationsLoaded', () => {
            setAnnotationsLoaded(true);
          });
        }
      });
    }
  }, []);

  useEffect(() => {
    if (annotationsLoaded && viewer) {
      loadRemoteAnnotations(
        annotationApi,
        viewer,
        previousSessionAnnotationIds,
        upload.canDownload,
      ).then(() => {
        setEditorStatus('loaded');
      });
      if (annotationApi.isAppendOnly) {
        const newDownloadButton = {
          dataElement: 'newDownloadButton',
          label: 'Download',
          onClick: () => handleDownload(viewer),
          icon: 'icon-download',
        };

        const mainMenuFlyout = viewer.UI.Flyouts.getFlyout('MainMenuFlyout');
        const mainMenuFlyoutItems = mainMenuFlyout.items;
        const downloadButtonIndex = mainMenuFlyoutItems.findIndex((item: any)=> item.dataElement === 'downloadButton');
        mainMenuFlyoutItems[downloadButtonIndex] = newDownloadButton;
        mainMenuFlyout.setItems(mainMenuFlyoutItems);
      }
    }
  }, [annotationsLoaded, viewer]);

  return { status: editorStatus };
}

export async function loadRemoteAnnotations(
  { data, authorRole, isAppendOnly }: AnnotationApi,
  webViewerInstance: WebViewerInstance,
  previousSessionAnnotationIds?: MutableRefObject<Set<string>>,
  isDownloadMode: boolean = false,
) {
  const isPrivileged = priviledgedUserRoles.includes(authorRole) || !isAppendOnly;
  const annotationCommands = data.filter((d) => d.isCommand);

  await Promise.all(
    annotationCommands.map(async (row) => {
      const annotations =
        await webViewerInstance.Core.annotationManager.importAnnotationCommand(
          row.xfdfValue!,
        );
      annotations.forEach((annot) => {
        annot.Locked = !isPrivileged || (isAppendOnly && isDownloadMode);
        annot.ReadOnly = !isPrivileged || (isAppendOnly && isDownloadMode);
        annot.LockedContents = !isPrivileged || (isAppendOnly && isDownloadMode);

        // Track this annotation as existing from a previous session
        if (previousSessionAnnotationIds && annot.Id) {
          previousSessionAnnotationIds.current.add(annot.Id);
        }
      });
      await webViewerInstance.Core.annotationManager.drawAnnotationsFromList(
        annotations,
      );
    }),
  );
}

async function onAnnotationChange(
  webViewerInstance: WebViewerInstance,
  apiSave: AnnotationApi['save'],
  annotations: Core.Annotations.Annotation[],
  action: string,
  info: Core.AnnotationManager.AnnotationChangedInfoObject,
  projectId?: number,
  annotationApi?: AnnotationApi,
  previousSessionAnnotationIds?: MutableRefObject<Set<string>>,
) {
  if (!info.imported) {
    await logAnnotationModification(
      annotations,
      action,
      annotationApi,
      previousSessionAnnotationIds,
      projectId,
    );

    await saveAnnotationCommands(
      webViewerInstance.Core.annotationManager,
      apiSave,
      projectId,
    );
  }
}

async function logAnnotationModification(
  annotations: Core.Annotations.Annotation[],
  action: string,
  annotationApi?: AnnotationApi,
  previousSessionAnnotationIds?: MutableRefObject<Set<string>>,
  projectId?: number,
) {
  const existingAnnotations = annotations.filter(
    (a) =>
      previousSessionAnnotationIds &&
      previousSessionAnnotationIds.current.has(a.Id),
  );

  const saveRequests: any[] = [];

  existingAnnotations.forEach((annot) => {
    saveRequests.push(
      annotationApi?.save({
        isModificationLog: true,
        action: action,
        key: annot.Id || '',
        xfdfValue: '',
        projectId,
      }),
    );
  });
}

async function saveAnnotationCommands(
  manager: Core.AnnotationManager,
  apiSave: AnnotationApi['save'],
  projectId?: number,
) {
  const xfdfString = await manager.exportAnnotationCommand();
  const parser = new DOMParser();
  const commandData = parser.parseFromString(xfdfString, 'application/xml');
  const addedAnnots = commandData.getElementsByTagName('add')[0];
  const modifiedAnnots = commandData.getElementsByTagName('modify')[0];
  const deletedAnnots = commandData.getElementsByTagName('delete')[0];
  const saveRequests: Promise<void>[] = [];

  // List of added annotations
  addedAnnots.childNodes.forEach((child) => {
    if (isElement(child) && hasTitleAttribute(child)) {
      const model = xfdfApi.getCommandModel(child, 'add', projectId);

      if (model.key) {
        saveRequests.push(apiSave(model));
      }
    }
  });

  // List of modified annotations
  modifiedAnnots.childNodes.forEach((child) => {
    if (isElement(child) && hasTitleAttribute(child)) {
      saveRequests.push(
        apiSave(xfdfApi.getCommandModel(child, 'modify', projectId)),
      );
    }
  });

  // List of deleted annotations
  deletedAnnots.childNodes.forEach((child) => {
    if (isElement(child)) {
      saveRequests.push(
        apiSave(xfdfApi.getCommandModel(child, 'delete', projectId)),
      );
    }
  });

  await Promise.all(saveRequests);
}

function isElement(child: ChildNode): child is Element {
  return child.nodeType == Node.ELEMENT_NODE;
}

function hasTitleAttribute(element: Element) {
  return (element.getAttribute('title') || '').length > 0;
}

function useProjectId() {
  const { project } = useContext(ProjectContext);

  if (project && project.id) {
    return project.id;
  }

  return undefined;
}
